import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { useAttributes, useFetchedValues } from "../../../../Hooks/Display/useComponentProperties";
import HyperlinkCell from "./Cells/HyperlinkCell";
import TargetCell from "./Cells/TargetCell";
import { usePortalPages } from "../../../../Hooks/Display/usePages";
import TooltipCell from "./Cells/TooltipCell";
import { usePropsStoreValue } from "../../../../Hooks/PropStore/usePropsStore";
import { useMemo, useState } from "react";
import { DraggableRow } from "./Row/DraggableRow";
import IDCell from "./Cells/IdCell";
import { handleAttributeGroupChange, reorderData, reorderDataForFormAndView } from "../utils";
import { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import NormalCell from "./Cells/NormalCell";
import AttributeType from "./Cells/AttributeType";
import GroupCell from "./Cells/GroupCell";
import ColorFormatting from "./Cells/ColorFormatting";
import { useTranslation } from "react-i18next";
import { sortAttributeGroups } from "../../../Attributes/attributeGroupUtils";

function AttributeTableGrid() {

    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner")
    const selectedComponent = useSelectedComponent();

    const portalPages = usePortalPages();
    const [fetchedValues] = useFetchedValues();
    const { attributeTable: { referenceAttributePages, attributes } } = fetchedValues;

    const [state, dispatch] = useAttributes();

    const [activeItem, setActiveItem] = useState(null);

    const targetPages = useMemo(() => (
        referenceAttributePages && referenceAttributePages.length > 0 ? portalPages.concat(referenceAttributePages) : portalPages
    ), [portalPages, referenceAttributePages]);

    const { t } = useTranslation("display/tables/attribute", { useSuspense: false });

    const singleRecordPages = targetPages.filter(page => page.subPageType === "Single Record");

    const entityAttributes = fetchedValues.attributeTable.attributes || [];

    const attributeGroups = sortAttributeGroups([
        ...new Set([
            ...entityAttributes.map(attribute => attribute.group),
            ...(state.attributes?.map(attribute => attribute.group) || []),
        ])
    ].filter(value => !!value));

    function toggleHyperlink(event, attribute) {
        dispatch.setAttributeProperty(attribute.attributeId, "hyperlink", event.target.value);
        if (event.target.value === true) {
            const targetEntityId = attribute.attributeType === "Entity Lookup" ? attribute.referencedEntityId : attribute.entityId;
            const targetPage = singleRecordPages.find(page => page.entityId === targetEntityId);
            if (targetPage) {
                dispatch.setAttributeProperty(attribute.attributeId, "targetPageId", targetPage._id);
            }
        } else {
            dispatch.setAttributeProperty(attribute.attributeId, "targetPageId", "");
        }
    }

    function toggleCellRules(event, attribute) {
        dispatch.setAttributeProperty(attribute.attributeId, "cellRules", event.target.value);
    }

    function handleSelect(event) {
        dispatch.selectAttributeRow(event.dataItem.attributeId);
    }

    function handleTargetPageChange(event, attribute) {
        dispatch.setAttributeProperty(attribute.attributeId, "targetPageId", event.target.value._id)
    }

    function handleTooltipChange(event, attribute) {
        dispatch.setAttributeProperty(attribute.attributeId, "tooltip", event.target.value)
    }

    function handleGroupChange(event, attribute) {
        const newData = handleAttributeGroupChange(state.attributes, attribute, event.target.value);
        dispatch.setAttributes(newData);
    }

    function dragStart(dataItem) {
        setActiveItem(dataItem);
    }

    function endDrag(dataItem, direction) {
        if (selectedComponent.componentType === "Form" || selectedComponent.componentType === "View") {
            const reorderedData = reorderDataForFormAndView(state.attributes, activeItem, dataItem, direction, true);
            dispatch.setAttributes(reorderedData);
        } else {
            const reorderedData = reorderData(state.attributes, activeItem, dataItem, direction);
            dispatch.setAttributes(reorderedData);
        }
    }

    const gridData = state.attributes.map((attribute, index) => {
        const entityAttr = entityAttributes.find(a => a._id === attribute.attributeId);
        return {
            ...attribute,
            group: entityAttr?.group ?? attribute.group ?? "",
            selected: attribute.attributeId === state.selectedAttributeId,
            "#": index + 1
        };
    });

    return (
        <Grid
            selectedField="selected"
            data={gridData}
            groupable={false}
            scrollable={false}
            dataItemKey="attributeId"
            rowRender={(row, rowProps) => (
                <DraggableRow
                    elementProps={row.props}
                    {...rowProps}
                    dragStart={dragStart}
                    reorder={endDrag}
                />
            )}
            onRowClick={handleSelect}
        >
            <GridColumn
                field="#" title="#" width="50px"
                cell={props => <IDCell {...props} hidden={selectedComponent.hidden} />}
            />
            <GridColumn
                field="attributeLabel"
                title={t("grid.column_name")}
                cell={props => <NormalCell {...props} hidden={selectedComponent.hidden} />}
            />
            {
                accessedFromDesigner &&
                <GridColumn
                    field="attributeType"
                    title={t("grid.column_type")}
                    cell={props => <AttributeType {...props} hidden={selectedComponent.hidden} />}
                />
            }
            {
                selectedComponent.componentType !== "Form" &&
                selectedComponent.componentType !== "View" &&
                <GridColumn
                    field="hyperlink"
                    title={t("grid.column_hyperlink")}
                    cell={(props) => (
                        <HyperlinkCell
                            {...props}
                            pages={singleRecordPages}
                            onChange={toggleHyperlink}
                            hidden={selectedComponent.hidden}
                        />
                    )}
                />
            }
            {
                (
                    selectedComponent.componentType === "Form" ||
                    selectedComponent.componentType === "View"
                ) &&
                <GridColumn
                    field="group"
                    title={t("grid.column_section")}
                    width={180}
                    cell={props => (
                        <GroupCell
                            {...props}
                            groups={attributeGroups}
                            onGroupChange={handleGroupChange}
                        />
                    )}
                />
            }
            {
                accessedFromDesigner &&
                selectedComponent.componentType !== "Form" &&
                selectedComponent.componentType !== "View" &&
                (
                    <GridColumn
                        field="target"
                        title={t("grid.column_target_page")}
                        cell={props => (
                            <TargetCell
                                {...props}
                                onChange={handleTargetPageChange}
                                hidden={selectedComponent.hidden}
                                pages={singleRecordPages}
                            />
                        )}
                    />
                )
            }
            <GridColumn
                field="cellRules"
                cell={(props) => (
                    <ColorFormatting
                        {...props}
                        hidden={selectedComponent.hidden}
                        onChange={toggleCellRules}
                    />
                )}
                headerCell={() => (
                    <>
                        <span>{t("grid.column_color_formatting")}</span>
                        <i className="fal fa-info-circle ms-2" title={t("grid.column_color_formatting_tooltip")} />
                    </>
                )}
            />
            {
                accessedFromDesigner &&
                selectedComponent.componentType !== "Form" &&
                selectedComponent.componentType !== "View" &&
                (
                    <GridColumn
                        field="tooltip"
                        title={t("grid.column_tooltip")}
                        cell={props => (
                            <TooltipCell
                                {...props}
                                hidden={selectedComponent.hidden}
                                onChange={handleTooltipChange}
                            />
                        )}
                    />
                )
            }
        </Grid>
    )
}

export default AttributeTableGrid;